import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  AlertTriangle, Building2, CalendarDays, CheckCircle2, ClipboardList, CreditCard,
  DollarSign, Home, LifeBuoy, PlaneLanding, PlaneTakeoff, Sparkles, TrendingUp, Wrench,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard, KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatChartCurrency } from "@/lib/format";

type ListReservation = { id: number; guestName: string };
type UrgentJob = { id: number; apartmentId: number | string | null; status: string };
type LowStockItem = { id: number; name: string; currentStock: number; reorderPoint: number };
type RecentSubmission = { id: number; guestName: string; timestamp: string | Date | null };
type RevenueAptRow = { id: string; name: string; total: number };

export type PropertyModeViewProps = {
  hideFinancials: boolean;

  // Key stats
  buildingsCount: number;
  apartmentsCount: number;
  reservationsCount: number;
  checkoutsCount: number;

  // Financial summary
  totalRevenue: number;
  pendingPayments: number;
  totalPaid: number;

  // Operations
  activeReservations: number;
  openTickets: number;
  activeMaintenance: number;

  // Cleaning
  completedCleanings: number;
  pendingCleanings: number;
  inProgressCleanings: number;
  unassignedCleanings: number;
  assignedCleanings: number;
  todayCleanings: number;
  totalCleaningIncome: number;
  totalMaintenanceIncome: number;

  // Today
  arrivals: ListReservation[];
  departures: ListReservation[];
  urgentJobs: UrgentJob[];
  lowStockData?: { lowStockCount: number; items: LowStockItem[] };

  // Right column
  revenueByApartment: RevenueAptRow[];
  recentActivity: RecentSubmission[];

  // Optional time-series for hero chart + KPI sparklines (last 30 days, daily)
  revenueTrend?: { date: string; value: number }[];
  bookingsTrend?: { date: string; value: number }[];
  jobsTrend?: { date: string; value: number }[];

  // Optional donut-chart breakdowns
  cleaningStatusBreakdown?: { name: string; value: number; color: string }[];
  bookingSourceBreakdown?: { name: string; value: number; color: string }[];
};

function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const REVENUE_BAR_COLORS = ["#6366f1", "#8b5cf6", "#a855f7", "#c026d3", "#db2777"];

export function PropertyModeView(props: PropertyModeViewProps) {
  const {
    hideFinancials,
    buildingsCount, apartmentsCount, reservationsCount, checkoutsCount,
    totalRevenue, pendingPayments, totalPaid,
    activeReservations, openTickets, activeMaintenance,
    completedCleanings, pendingCleanings, inProgressCleanings, unassignedCleanings,
    assignedCleanings, todayCleanings, totalCleaningIncome, totalMaintenanceIncome,
    arrivals, departures, urgentJobs, lowStockData,
    revenueByApartment, recentActivity,
    revenueTrend, bookingsTrend, jobsTrend,
    cleaningStatusBreakdown, bookingSourceBreakdown,
  } = props;

  const cleaningDonutData = (cleaningStatusBreakdown ?? []).filter(s => s.value > 0);
  const bookingDonutData = (bookingSourceBreakdown ?? []).filter(s => s.value > 0);
  const showDonuts = cleaningDonutData.length > 0 || bookingDonutData.length > 0;

  const heroTrend = (revenueTrend ?? []).map(r => ({ ...r, label: shortDay(r.date) }));
  const showHero = !hideFinancials && heroTrend.length > 0 && heroTrend.some(d => d.value > 0);

  return (
    <>
      <MetricStrip cols={4}>
        <KpiCard label="Total Buildings" value={buildingsCount} icon={Building2} variant="info" testId="card-stat-buildings" />
        <KpiCard label="Active Apartments" value={apartmentsCount} icon={Home} variant="success" testId="card-stat-apartments" />
        <KpiCard
          label="Reservations"
          value={reservationsCount}
          icon={CalendarDays}
          variant="default"
          sparkline={bookingsTrend?.map(b => b.value)}
          testId="card-stat-reservations"
        />
        <KpiCard
          label="Checkouts"
          value={checkoutsCount}
          icon={TrendingUp}
          variant="warning"
          sparkline={jobsTrend?.map(b => b.value)}
          testId="card-stat-checkouts"
        />
      </MetricStrip>

      {!hideFinancials && (
        <MetricStrip cols={3}>
          <KpiCard
            label="Total Revenue"
            value={formatCurrency(totalRevenue)}
            icon={DollarSign}
            variant="success"
            sparkline={revenueTrend?.map(b => b.value)}
            testId="card-total-revenue"
          />
          <KpiCard label="Pending Payments" value={formatCurrency(pendingPayments)} icon={CreditCard} variant="warning" testId="card-pending-payments" />
          <KpiCard label="Total Paid" value={formatCurrency(totalPaid)} icon={CheckCircle2} variant="info" testId="card-paid-this-month" />
        </MetricStrip>
      )}

      {showHero && (
        <ChartCard
          title="Revenue trend"
          description="Daily revenue for the last 30 days"
          height={260}
          testId="card-dashboard-revenue-trend"
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={heroTrend} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="hero-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}`} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => formatCurrency(v)} />
              <Area type="monotone" dataKey="value" name="Revenue" stroke="#10b981" fill="url(#hero-rev)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <MetricStrip cols={3}>
        <KpiCard label="Active Reservations" value={activeReservations} icon={CalendarDays} variant="default" testId="card-active-reservations" />
        <KpiCard label="Open Tickets" value={openTickets} icon={LifeBuoy} variant="warning" testId="card-open-tickets" />
        <KpiCard label="Maintenance Active" value={activeMaintenance} icon={Wrench} variant="info" testId="card-maintenance-active" />
      </MetricStrip>

      <Card data-testid="card-cleaning-operations">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Cleaning Operations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MetricStrip cols={4} className="gap-2">
            <KpiCard label="Completed" value={completedCleanings} icon={CheckCircle2} variant="success" />
            <KpiCard label="Pending" value={pendingCleanings} icon={ClipboardList} variant="warning" />
            <KpiCard label="In Progress" value={inProgressCleanings} icon={Sparkles} variant="info" />
            <KpiCard label="Unassigned" value={unassignedCleanings} icon={AlertTriangle} variant="danger" />
          </MetricStrip>
          <div className={`grid gap-3 mt-4 pt-4 border-t ${hideFinancials ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`}>
            <SubStat label="Assigned" value={String(assignedCleanings)} testId="stat-cleaning-assigned" />
            <SubStat label="Today's Jobs" value={String(todayCleanings)} testId="stat-cleaning-today" />
            {!hideFinancials && (
              <>
                <SubStat label="Cleaning Billings" value={formatCurrency(totalCleaningIncome)} accent testId="stat-cleaning-revenue" />
                <SubStat label="Maintenance Billings" value={formatCurrency(totalMaintenanceIncome)} accent testId="stat-maintenance-revenue" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3" data-testid="section-today">
        <TodayCard
          icon={<PlaneLanding className="w-5 h-5 text-primary" />}
          iconBg="bg-primary/10"
          label="Arrivals Today"
          count={arrivals.length}
          emptyText="No arrivals today"
          testId="card-arrivals-today"
        >
          {arrivals.map(r => (
            <p key={r.id} className="text-sm truncate" data-testid={`text-arrival-guest-${r.id}`}>{r.guestName}</p>
          ))}
        </TodayCard>

        <TodayCard
          icon={<PlaneTakeoff className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
          iconBg="bg-blue-500/10"
          label="Departures Today"
          count={departures.length}
          emptyText="No departures today"
          testId="card-departures-today"
        >
          {departures.map(r => (
            <p key={r.id} className="text-sm truncate" data-testid={`text-departure-guest-${r.id}`}>{r.guestName}</p>
          ))}
        </TodayCard>

        <TodayCard
          icon={<AlertTriangle className="w-5 h-5 text-destructive" />}
          iconBg="bg-destructive/10"
          label="Urgent Cleaning"
          count={urgentJobs.length}
          emptyText="No urgent jobs"
          accent={urgentJobs.length > 0}
          testId="card-urgent-cleaning"
        >
          {urgentJobs.slice(0, 5).map(job => (
            <div key={job.id} className="flex items-center justify-between gap-2" data-testid={`urgent-job-${job.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-3 h-3 text-destructive shrink-0" />
                <span className="text-sm truncate">Apt #{job.apartmentId}</span>
              </div>
              <Badge variant="destructive" data-testid={`badge-urgent-status-${job.id}`}>{job.status}</Badge>
            </div>
          ))}
        </TodayCard>

        {lowStockData && lowStockData.lowStockCount > 0 && (
          <Card className="border-yellow-300 dark:border-yellow-700" data-testid="card-low-stock-alert">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Low Stock Supplies</p>
                  <p className="text-xl sm:text-2xl font-bold" data-testid="text-low-stock-count">{lowStockData.lowStockCount}</p>
                </div>
              </div>
              <div className="space-y-2">
                {lowStockData.items.slice(0, 5).map(item => (
                  <div key={item.id} className="flex items-center justify-between gap-2" data-testid={`low-stock-item-${item.id}`}>
                    <span className="text-sm truncate">{item.name}</span>
                    <Badge variant="outline" className="text-yellow-600 border-yellow-300 text-[10px]">
                      {item.currentStock}/{item.reorderPoint}
                    </Badge>
                  </div>
                ))}
              </div>
              <a href="/admin/inventory" className="text-xs text-primary mt-3 block hover:underline" data-testid="link-view-inventory">
                View Inventory →
              </a>
            </CardContent>
          </Card>
        )}
      </div>

      {showDonuts && (
        <div className="grid gap-6 md:grid-cols-2">
          {cleaningDonutData.length > 0 && (
            <ChartCard
              title="Cleaning by status"
              description={`${cleaningDonutData.reduce((s, x) => s + x.value, 0)} jobs in period`}
              height={240}
              testId="card-cleaning-status-donut"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cleaningDonutData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ""} ${(((percent ?? 0) * 100)).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {cleaningDonutData.map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => `${v} jobs`} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {bookingDonutData.length > 0 && (
            <ChartCard
              title="Bookings by source"
              description={`${bookingDonutData.reduce((s, x) => s + x.value, 0)} reservations in period`}
              height={240}
              testId="card-booking-source-donut"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bookingDonutData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ""} ${(((percent ?? 0) * 100)).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {bookingDonutData.map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => `${v} bookings`} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      <div className={`grid gap-6 ${!hideFinancials ? "md:grid-cols-2" : ""}`}>
        {!hideFinancials && (
          <ChartCard
            title="Revenue by Apartment"
            description="Top 5 by total billings in the selected period"
            isEmpty={revenueByApartment.length === 0}
            emptyText="No financial records yet"
            height={Math.max(180, revenueByApartment.length * 36 + 24)}
            testId="card-revenue-by-apartment"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueByApartment} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatChartCurrency} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  cursor={{ fill: "transparent" }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {revenueByApartment.map((_, i) => (
                    <Cell key={i} fill={REVENUE_BAR_COLORS[i % REVENUE_BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <Card data-testid="card-recent-activity">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-muted-foreground" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.slice(0, 5).map(sub => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between gap-4 border-b pb-4 last:border-0 last:pb-0 flex-wrap"
                  data-testid={`activity-${sub.id}`}
                >
                  <div>
                    <p className="font-medium" data-testid={`text-activity-name-${sub.id}`}>{sub.guestName}</p>
                    <p className="text-sm text-muted-foreground">Checked out</p>
                  </div>
                  <div className="text-sm text-muted-foreground" data-testid={`text-activity-date-${sub.id}`}>
                    {sub.timestamp ? new Date(sub.timestamp).toLocaleDateString("en-US", { timeZone: "UTC" }) : "N/A"}
                  </div>
                </div>
              ))}
              {recentActivity.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function SubStat({ label, value, accent, testId }: { label: string; value: string; accent?: boolean; testId?: string }) {
  return (
    <div className="text-center" data-testid={testId}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}

type TodayCardProps = {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  count: number;
  emptyText: string;
  accent?: boolean;
  testId: string;
  children?: React.ReactNode;
};

function TodayCard({ icon, iconBg, label, count, emptyText, accent, testId, children }: TodayCardProps) {
  return (
    <Card className={accent ? "border-destructive" : ""} data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-xl sm:text-2xl font-bold">{count}</p>
          </div>
        </div>
        {count > 0 ? <div className="space-y-2">{children}</div> : <p className="text-sm text-muted-foreground">{emptyText}</p>}
      </CardContent>
    </Card>
  );
}

export default PropertyModeView;
